import React, { useCallback, useEffect, useState } from "react";
import { FolderArchive, ChevronRight, ChevronDown, Loader2, FileText, Image as ImageIcon, Mic, Film, Paperclip, CalendarClock, CalendarCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";

function iconForFormat(fmt) {
  if (fmt === "photo") return ImageIcon;
  if (fmt === "voice" || fmt === "audio") return Mic;
  if (fmt === "video") return Film;
  if (fmt === "pdf" || fmt === "document") return FileText;
  return Paperclip;
}

function ArchiveNode({ archive, matterId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [incidents, setIncidents] = useState(null);
  const [linkId, setLinkId] = useState("");
  const [linking, setLinking] = useState(false);

  const toggleLink = async (e) => {
    e.stopPropagation();
    const next = !linkOpen;
    setLinkOpen(next);
    if (next && incidents === null) {
      try {
        const { data } = await api.get("/incidents", { params: matterId ? { matter_id: matterId } : {} });
        setIncidents(data || []);
      } catch { setIncidents([]); }
    }
  };

  const doLink = async () => {
    if (!linkId) return;
    setLinking(true);
    try {
      const { data } = await api.post(`/evidence/archives/${archive.id}/link-to-incident`, { incident_id: linkId });
      toast.success(
        data.linked > 0 ? `Linked ${data.linked} file${data.linked === 1 ? "" : "s"} to the record` : "All files were already linked to that record",
        { description: data.already ? `${data.already} already linked` : undefined });
      setLinkOpen(false); setLinkId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not link files");
    } finally { setLinking(false); }
  };

  const loadChildren = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/evidence/archives/${archive.id}/children`);
      setChildren(data.items || []);
    } catch { setChildren([]); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && children === null) await loadChildren();
  };

  const autoConfirm = async (e) => {
    e.stopPropagation();
    setConfirming(true);
    try {
      const { data } = await api.post(`/evidence/archives/${archive.id}/auto-confirm-dates`);
      if (data.confirmed > 0) {
        const extra = [
          data.conflicts ? `${data.conflicts} need manual review (conflicting dates)` : "",
          data.no_date ? `${data.no_date} had no detected date` : "",
        ].filter(Boolean).join(" · ");
        toast.success(`Confirmed ${data.confirmed} high-confidence date${data.confirmed === 1 ? "" : "s"}`, { description: extra || undefined });
      } else {
        toast.message("No high-confidence dates to confirm", {
          description: data.conflicts ? `${data.conflicts} item(s) have conflicting dates — confirm those manually.` : "These files had no unambiguous detected date.",
        });
      }
      if (open) await loadChildren();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not confirm dates");
    } finally { setConfirming(false); }
  };

  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }} data-testid={`archive-node-${archive.id}`}>
      <div className="w-full flex items-center gap-2.5 px-3 py-2.5">
        <button onClick={toggle} data-testid={`archive-toggle-${archive.id}`} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
          {open ? <ChevronDown className="h-4 w-4 shrink-0" style={{ color: "var(--ek-text-muted)" }} /> : <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "var(--ek-text-muted)" }} />}
          <FolderArchive className="h-4 w-4 shrink-0" style={{ color: "var(--ek-primary)" }} />
          <span className="text-sm truncate" data-private>{archive.filename}</span>
          <span className="ek-mono-label shrink-0" data-testid={`archive-count-${archive.id}`}>{archive.child_count} file{archive.child_count === 1 ? "" : "s"}</span>
        </button>
        {archive.child_count > 0 && (
          <button onClick={toggleLink} disabled={linking} data-testid={`archive-link-${archive.id}`}
            title="Attach all files in this archive to one timeline record"
            className="inline-flex items-center gap-1 text-xs rounded-md border px-2 py-1 shrink-0 transition-colors hover:border-slate-500 disabled:opacity-60"
            style={{ borderColor: linkOpen ? "var(--ek-primary)" : "var(--ek-border)", color: "var(--ek-primary)", background: "var(--ek-surface)" }}>
            <Link2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Link to record</span>
          </button>
        )}
        {archive.child_count > 0 && (
          <button onClick={autoConfirm} disabled={confirming} data-testid={`archive-autoconfirm-${archive.id}`}
            title="Confirm all high-confidence dates in this archive"
            className="inline-flex items-center gap-1 text-xs rounded-md border px-2 py-1 shrink-0 transition-colors hover:border-slate-500 disabled:opacity-60"
            style={{ borderColor: "var(--ek-border)", color: "var(--ek-primary)", background: "var(--ek-surface)" }}>
            {confirming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarCheck className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">Confirm dates</span>
          </button>
        )}
      </div>
      {linkOpen && (
        <div className="border-t px-3 py-2.5 flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }} data-testid={`archive-link-panel-${archive.id}`}>
          {incidents === null ? (
            <span className="text-xs flex items-center gap-2" style={{ color: "var(--ek-text-muted)" }}><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading records…</span>
          ) : incidents.length === 0 ? (
            <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>No timeline records in this case yet — create one first.</span>
          ) : (
            <>
              <select
                value={linkId}
                onChange={(e) => setLinkId(e.target.value)}
                data-testid={`archive-link-select-${archive.id}`}
                className="rounded-md border px-2 py-1.5 text-xs flex-1 min-w-[180px]"
                style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)", color: "var(--ek-text)" }}>
                <option value="">Choose a record…</option>
                {incidents.map((it) => (
                  <option key={it.id} value={it.id}>
                    {(it.occurred_at ? new Date(it.occurred_at).toLocaleDateString() + " · " : "") + ((it.pure_fact || it.fact_only || "Record").slice(0, 60))}
                  </option>
                ))}
              </select>
              <button onClick={doLink} disabled={!linkId || linking} data-testid={`archive-link-confirm-${archive.id}`}
                className="inline-flex items-center gap-1 text-xs rounded-md px-3 py-1.5 shrink-0 disabled:opacity-50"
                style={{ background: "var(--ek-primary)", color: "#fff" }}>
                {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Link {archive.child_count} file{archive.child_count === 1 ? "" : "s"}
              </button>
            </>
          )}
        </div>
      )}
      {open && (
        <div className="border-t divide-y" style={{ borderColor: "var(--ek-border)" }} data-testid={`archive-children-${archive.id}`}>
          {loading && (
            <p className="px-3 py-3 text-xs flex items-center gap-2" style={{ color: "var(--ek-text-muted)" }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading extracted files…
            </p>
          )}
          {!loading && children && children.length === 0 && (
            <p className="px-3 py-3 text-xs" style={{ color: "var(--ek-text-muted)" }}>No files were extracted from this archive.</p>
          )}
          {!loading && children && children.map((c) => {
            const Icon = iconForFormat(c.format);
            return (
              <div key={c.id} className="flex items-center gap-2.5 px-3 py-2 pl-9" data-testid={`archive-child-${c.id}`}>
                <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
                <span className="text-xs truncate flex-1" data-private>{c.filename}</span>
                {c.date_review_needed && c.date_status !== "confirmed" && (
                  <span className="inline-flex items-center gap-1 text-[10px] shrink-0" style={{ color: "var(--ek-warning)" }} title="Event date needs confirmation">
                    <CalendarClock className="h-3 w-3" /> date review
                  </span>
                )}
                {c.is_duplicate && <span className="ek-mono-label shrink-0" style={{ color: "var(--ek-text-muted)" }}>dup</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ArchiveTree({ matterId, section = "user_evidence", refreshKey = 0 }) {
  const [archives, setArchives] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ section });
      if (matterId) params.set("matter_id", matterId);
      const { data } = await api.get(`/evidence/archives?${params.toString()}`);
      setArchives(data.archives || []);
    } catch { /* non-fatal */ }
  }, [matterId, section]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (archives.length === 0) return null;

  return (
    <div className="mb-6 border rounded-lg p-4" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }} data-testid="archive-tree">
      <button onClick={() => setCollapsed((v) => !v)} data-testid="archive-tree-toggle" className="w-full flex items-center gap-2 mb-1">
        {collapsed ? <ChevronRight className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} /> : <ChevronDown className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} />}
        <FolderArchive className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
        <span className="font-head font-semibold text-sm tracking-tight">Imported Archives</span>
        <span className="ek-mono-label ml-auto">{archives.length} archive{archives.length === 1 ? "" : "s"}</span>
      </button>
      {!collapsed && (
        <>
          <p className="text-xs mb-3" style={{ color: "var(--ek-text-muted)" }}>Expand a ZIP to browse the files extracted from it. Every file is preserved as its own encrypted record.</p>
          <div className="space-y-2">
            {archives.map((a) => <ArchiveNode key={a.id} archive={a} matterId={matterId} onChanged={load} />)}
          </div>
        </>
      )}
    </div>
  );
}
